import firebase from "firebase/app";
import {serializer} from "@/api/shared/serializer";
import {GalleryFolder} from "@/models/GalleryFolder";
import Loader from "@/components/loader/Loader.vue";
import {deleteFileByUrl, optimizePhotos, uploadImageAsync} from "@/utils/upload-image";
import ConfirmDlg from '@/components/popup/confirm-dialog/ConfirmDlg.vue';
import { v4 as uuidv4 } from "uuid";
import GalleryShareDlg from "@/views/staff/gallery/GalleryShareDlg/GalleryShareDlg.vue";
import SharePhotoDlg from "@/views/staff/gallery/SharePhotoDlg/SharePhotoDlg.vue";
import {waitResizedImage} from "@/utils/wait-resized-image";

export default {
  name: "GalleryFolderView",
  components: {
    Loader,
    ConfirmDlg,
    GalleryShareDlg,
    SharePhotoDlg
  },
  data() {
    return {
      name: '',
      loading: true,
      imgLoading: false,
      selectedPhotos: [],
      addMediaDialog: false
    }
  },
  computed: {
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    staffUser() {
      return this.$store.state.authModule.user;
    },
    photos() {
      return this.$store.state.galleryModule.galleryFolderPhotos;
    }
  },
  async created() {
    await this.getCurrentGalleryFolder();
    await this.$store.dispatch("getGalleryFolderPhotos", this.$route.params.id)
    this.loading = false;
  },
  methods: {
    async getCurrentGalleryFolder() {
      firebase
        .firestore()
        .collection("galleryFolders")
        .doc(this.$route.params.id)
        .get()
        .then((data) => {
          const folder: GalleryFolder = serializer.documentSnapshotToDocument(data);
          this.name = folder.name;
        })
    },
    async saveEventImage(e) {
      this.imgLoading = true;
      const files = e.target.files;

      if (files.length) {
        const uploadPromises = [];

        for (let i = 0; i < files.length; i++) {
          const file: any = await optimizePhotos(files[i], 'background-image');
          const path = `${this.facility.id}/photos/staff/${uuidv4()}`;

          const uploadPromise = uploadImageAsync({ path, file }).then(async (storage) => {
            const url = await storage.ref.getDownloadURL();
            const id = String(new Date().getTime());

            const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);
            const resizedImageUrl = await waitResizedImage(10, resizedImageRef);

            const data = {
              createdAt: new Date().getTime(),
              facilityId: this.facility.id,
              image: url,
              thumbnailUrl: resizedImageUrl,
              isStaff: true,
              isVisibleForStaffGallery: false,
              sentBy: this.staffUser.name,
              sentById: this.staffUser.id,
              residentId: null,
              isUploaded: true,
              folderId: this.$route.params.id
            };

            await firebase
              .firestore()
              .collection("photos")
              .doc(id)
              .set(data);

            return true;
          });

          uploadPromises.push(uploadPromise);
        }

        try {
          await Promise.all(uploadPromises).then(() => {
            this.getPhotos();
            this.$toast.open({
              message: files.length > 1 ? this.$t('Gallery.SuccessUploadingImages') : this.$t('Gallery.SuccessUploadingImage'),
              type: "success"
            });
            this.clearInputFile();
          })
        } catch (error) {
          console.error("Error uploading images:", error);
        } finally {
          this.imgLoading = false;
        }
      }
    },
    clearInputFile() {
      const fileInput = document.getElementById('fileInput') as HTMLInputElement;
      fileInput.value = "";
    },
    onPhotoSelect(isSelected, photo) {
      if (isSelected) {
        this.selectedPhotos.push(photo)
      }
      else {
        this.selectedPhotos = this.selectedPhotos.filter(x => x.id != photo.id)
      }
    },
    openDialog(event, photo) {
      if (event.target.className.includes('selection-controls')) return;

      this.$refs.galleryShareDlg.open(photo);
    },
    async deletePhoto() {
      if (this.selectedPhotos.length <= 0) return

      this.$refs.confirmationModal
        .open(null,
          this.selectedPhotos.length > 1 ? this.$i18n.messages[this.$i18n.locale]['Gallery.AreYouSureYouWantToDeleteThesePicturesMessage'] :
            this.$i18n.messages[this.$i18n.locale]['Gallery.AreYouSureYouWantToDeleteThisPictureMessage'], false)
        .then(async (response) => {
          if (response && response.agree) {
            await this.deletePhotos(this.selectedPhotos)
          }
        });
    },
    async deletePhotos(photos) {
      this.loading = true;

      for (const photo of photos) {
        await firebase
          .firestore()
          .collection('photos')
          .doc(photo.id)
          .delete()
          .then(async () => {
            await this.$store.dispatch("getGalleryFolderPhotos", this.$route.params.id)

            await deleteFileByUrl(photo.image)
              .then(async () => {
                const str = photo.image;
                const from = 0;
                const to = str.search('alt') - 1;
                const first = str.substring(from, to) + '_200x200';
                const second = str.substring(to, str.length);
                const url = first + second;
                await deleteFileByUrl(url);

                this.selectedPhotos = this.selectedPhotos.filter(x => x.id != photo.id)
              })
              .catch(() => {
                this.$toast.open({
                  message: this.$i18n.messages[this.$i18n.locale]['Gallery.PhotoCannotBeDeletedMessage'],
                  type: 'error',
                });
              });
          })
          .catch(() => {
            this.$toast.open({
              message: this.$i18n.messages[this.$i18n.locale]['Gallery.PhotoCannotBeDeletedMessage'],
              type: 'error',
            });
          });
      }

      this.loading = false;
      this.addMediaDialog = false;

      this.$toast.open({
        message: photos.length > 1? this.$i18n.messages[this.$i18n.locale]['Gallery.PhotosSuccessfullyDeleted'] : this.$i18n.messages[this.$i18n.locale]['Gallery.PhotoSuccessfullyDeleted'],
        type: 'success',
      });

    },
    async getPhotos(photo) {
      this.selectedPhotos = this.selectedPhotos.filter(x => x.id != photo?.id);

      await this.$store.dispatch("getGalleryFolderPhotos", this.$route.params.id)
    },
    sharePhotoByEmail(photo) {
      this.$refs.sharePhotoDlg.open(photo);
    },
    async deleteFolder() {
      await firebase
        .firestore()
        .collection("galleryFolders")
        .doc(this.$route.params.id)
        .delete()
        .then(() => {
          this.$toast.open({
            message: this.$t("Gallery.FolderHasBeenDeleted"),
            type: "success"
          });

          this.$router.push("/gallery");
        }).catch((error) => {
        this.$toast.open({
          message: this.$t("Gallery.ErrorFolderDeleting"),
          type: "error"
        })
      })
    }
  }
}
